// chatConstants.js — single source of truth for constants shared across the
// desktop (FloorChat.js) and mobile (SwipeCards3.js) trading-floor chat surfaces,
// and their chat-card components (MessageReactions, MarketChip).
// Must stay in sync with backend/floor_chat.py.

// Mirrors backend/floor_chat.py REACTION_EMOJIS — the backend rejects any emoji
// not in this list (POST /floor-chat/react), so the two must match exactly.
export const REACTION_EMOJIS = ["👍", "🔥", "📈", "📉", "💀"];

// A slightly wider palette for free-form emoji picking in the ask bar (not
// restricted to reaction emoji, since those insert into message text rather
// than attach as a structured reaction).
export const EMOJI_PICKER_SET = [
  "😀", "😂", "😅", "😎", "🤔", "😬", "😭", "🥳",
  "👍", "👎", "🙌", "🤝", "🙏", "👀", "💪", "🔥",
  "🚀", "📈", "📉", "💰", "💸", "💀", "🎯", "⚡",
  "✅", "❌", "⚠️", "🧠", "🐂", "🐻", "🎉", "😴",
];

// Mirrors backend/floor_chat.py _SCENE_TYPES (20 entries) — keep in sync.
export const SCENE_TYPES = [
  "market_commentary",
  "trade_discussion",
  "risk_check",
  "news_reaction",
  "earnings_prep",
  "pnl_banter",
  "research_request",
  "regime_update",
  "crypto_chat",
  "yield_desk_talk",
  "inter_room",
  "ace_briefing",
  "flow_alert",
  "greeks_discussion",
  "sniper_setup",
  "trade_proposal",
  "funding_request",
  "trade_review",
  "goal_directive",
  "capital_cycle",
];

// Common uppercase words extractTickers would otherwise grab that are NOT tickers.
// Union of the two lists that previously drifted independently (floorHelpers.js's
// TICKER_SKIP and MarketChip.js's STOP) — one list now feeds both call sites.
export const TICKER_SKIP = new Set([
  "EV", "IV", "DTE", "HMM", "CIO", "CRO", "CEO", "CFO", "CTO", "COO",
  "RSI", "MACD", "SMA", "EMA", "VWAP", "ATM", "OTM", "ITM",
  "EPS", "FCF", "FFO", "DGR", "POP", "YOY", "QOQ", "YTD", "BPS",
  "CPI", "PPI", "PCE", "PMI", "ISM", "GDP", "FED", "SEC", "IPO",
  "LOD", "HOD", "AH", "PM", "RTH", "DCF", "HI", "LO", "AI", "ML",
  "OR", "IF", "AT", "BY", "TO", "IN", "ON", "IS", "IT", "BE",
  "US", "EU", "UK", "EM", "PD", "TP", "SL", "TF", "DD", "TA", "FA",
  "AND", "THE", "FOR", "ARE", "WAS", "BUT", "NOT", "YOU", "ALL", "CAN",
  "OUT", "DAY", "GET", "HAS", "NEW", "NOW", "OLD", "SEE", "TWO", "WAY",
  "WHO", "LET", "PUT", "SAY", "TOO", "USE", "OUR", "HOW",
  "WR", "EOD", "EOW", "TTM", "ATH", "ATL", "PE", "ROE", "ROI", "PT",
  "USD", "EUR", "GBP", "ETF", "FOMC", "IRS", "YOLO", "FOMO", "HODL",
  "LFG", "GG", "WTF", "LOL", "OMG", "IMO", "OG", "OK",
  "QQ", "Q1", "Q2", "Q3", "Q4", "H1", "H2", "FY", "EOY", "MOM",
  "BULL", "BEAR", "CALL", "LONG", "RISK", "PNL", "AUM", "NAV",
  "BUY", "SELL", "HOLD", "STOP", "BID", "ASK", "VOL",
  "DCA", "NFP", "REIT", "OPEX", "SPAC", "LEAP", "LEAPS",
  "THETA", "GAMMA", "DELTA", "VEGA", "DXY", "MORE", "SOME", "MOST",
  "THAN", "THEN", "THIS", "THAT", "WITH", "FROM", "INTO",
  "OVER", "ONLY", "JUST", "VERY", "MUCH", "LESS", "GOOD", "BEST",
  "HOT", "BIG", "YES", "OTC", "WSB", "ER", "GUAP", "BAG", "BAGS",
  "REKT", "MOON", "DIP", "RIP", "EOM", "EOQ",
  // Clock/timezone tokens. "[8:29 AM ET zB+38 5W-0L]" — the real-activity
  // prefix on fallback-scene messages — rendered $AM (Antero Midstream) and
  // $ET (Energy Transfer) chips, each fetching a quote for a stock nobody
  // mentioned. Seen live in the Diamond UI audit.
  "AM", "ET", "EST", "EDT", "UTC", "GMT", "ZB",
]);

export function extractTickers(text) {
  // Bracketed status tags ("[8:29 AM ET last NFLX]") are the agent's own
  // ledger/clock readout, never a ticker mention — drop them before matching.
  const cleaned = String(text ?? "").replace(/\[[^\]]*\]/g, " ");
  const matches = cleaned.match(/\b[A-Z]{2,5}\b/g) || [];
  return [...new Set(matches.filter((t) => !TICKER_SKIP.has(t)))];
}
